// Percorso: src/pages/Enrol.jsx
//
// LA PAGINA D'ISCRIZIONE sul modello nuovo: la SCELTA DEL PERCORSO e il
// PERCORSO A — L'ADULTO CHE SI ISCRIVE DA SÉ (RL-4, esteso da RL-5a).
//
// RL-4 aveva portato qui il solo percorso A. RL-5a mette davanti a tutto la
// scelta fra i due percorsi — «mi iscrivo io» e «iscrivo uno o più minori
// della stessa famiglia» — e affianca al ramo adulto il ramo famiglia, che
// vive in `EnrolFamily.jsx` e parla con `POST /enrol-family`.
//
//   Enrol            = la pagina: tiene la scelta e lo stato dei due moduli
//   SceltaPercorso   = il primo passo, le due schede
//   RamoAdulto       = il percorso A di RL-4, invariato nel comportamento
//   EnrolFamily      = il percorso B (file a parte)
//
// ⚠️ LO STATO DEI DUE MODULI VIVE QUI, nella pagina, e non dentro i rami: chi
// torna alla scelta e cambia idea non deve ritrovare il modulo svuotato. I
// rami tengono per sé solo ciò che è effimero (campi toccati, errore in corso,
// invio in corso, esito).
//
// È la controparte di frontend del `POST /enrol` nato in RL-3b. Non è un
// restyling di Register.jsx: è la stessa FUNZIONE rifatta sul modello nuovo.
//
// ⚠️ LA VESTE È DEL CANTIERE «GRAFICA ENROLLMENT» (4/9/2026). Fino ad allora
// questa pagina girava sulle classi di `Register.css` — un foglio senza un solo
// gettone del tema — e si mostrava come una card che galleggiava su un fondo
// nero. Ora è una PAGINA: fondo `--qpsc-bg`, colonna centrata §4, logo e titolo
// in testa, e ogni componente preso dal tema (doc 07 §§5-8). Le funzioni, i
// testi, la navigazione e la validazione NON sono stati toccati.
//
// CHE COSA CAMBIA RISPETTO A Register.jsx (rimosso da RL-5b: da quel cantiere
// questa è l'unica porta d'iscrizione dell'app, e il confronto qui sotto vale
// come memoria di che cosa il modulo vecchio faceva)
//
//   • NIENTE PASSWORD e nessuna `createUserWithEmailAndPassword`. Strada (b),
//     decisione D9 del 14/8/2026: l'iscrizione crea SOLO il documento in
//     anagrafica (`firebaseUid` a null), e l'utenza Firebase nascerà al primo
//     accesso col magic link (RL-8). Non c'è credenziale da creare qui,
//     quindi non c'è nemmeno la macchina crea-utenza/rollback del modulo
//     vecchio: il rollback cieco (reperto 28) non ha più nulla da annullare.
//
//   • NIENTE UPLOAD di documenti e foto: sono materia della finestra di
//     completamento (RL-7). Un modulo che li chiedesse qui prometterebbe una
//     consegna che il modello nuovo non registra ancora.
//
//   • SOLO I CAMPI DI D5: nome, cognome, email, data di nascita, genere M/F,
//     ruolo Player/Coach, accettazione dei termini. Niente ffaNumber (D11: sta
//     nel profilo, facoltativo), niente telefono, niente indirizzo, niente
//     contatti di emergenza (D12), niente `biometric*`/`deviceId`/
//     `authProvider`/`parentUid`: il costruttore v2 non li legge, e mandarli
//     sarebbe rumore che finge di essere dato.
//
//   • NESSUN TOKEN da allegare: `POST /enrol` è pubblica (chi si iscrive non
//     ha ancora un'utenza). `api.post` allega l'Authorization solo se esiste
//     una sessione Firebase, e su questa pagina non può esistercene una: la
//     rotta è dietro `PublicRoute`, che manda via chi è già collegato.
//
//   • NESSUN ACCESSO AUTOMATICO dopo l'iscrizione, e nessun atterraggio su
//     /home: non c'è sessione da aprire. La vista d'attesa /home/pending è
//     dietro `RequireRole`, quindi chi si è appena iscritto non può nemmeno
//     raggiungerla. L'atterraggio è il pannello di conferma qui sotto.
//
// LE VALIDAZIONI DI QUESTA PAGINA SONO CORTESIA, NON LEGGE. L'autorità è il
// server: lo sbarramento dei minorenni, il vocabolario dei ruoli e il
// duplicato di email vivono in `POST /enrol`, che è pubblica e risponde allo
// stesso modo anche a chi la chiama a mano. Qui si evita solo alla persona il
// giro inutile.

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/api";
// ⚠️ RL-8c: qui si importava `PUBLIC_APP_URL` (src/config.js), che serviva
// SOLO alla frase «you will receive an email with the instructions to access
// the app at ...», tolta perché quell'email non parte. Chi legge questo
// pannello è già dentro l'app: l'indirizzo non ha più niente da dirgli.
import EnrolFamily, { famigliaVuota } from "./EnrolFamily";
// Le funzioni qui sotto stavano in questo file da RL-4: RL-5a le ha spostate
// in `enrolShared.jsx` senza cambiarne una riga, perché il ramo famiglia le usa
// identiche e due copie sarebbero divergite alla prima correzione.
import {
  GENERI,
  emailBenFormata,
  etaCompiuta,
  messaggioDiErrore,
  oggiAPerth,
} from "../utils/enrolDati";
import { LinkTermini, TornaAllaScelta } from "./enrolShared";
// LA VESTE (cantiere «grafica enrollment», passo 1). `Register.css` è stato
// ritirato: la pagina prende ora le classi del tema.
//   HomeShell.css  — il guscio (`.qpsc-page`, `.qpsc-column`), il titolo
//                    (`.qpsc-page-title`), il gruppo-scelta §6 e il pulsante
//                    scuro §5.2. Si importa il FOGLIO, non il componente
//                    `HomeShell`: questa è una pagina PUBBLICA e non porta né
//                    barra utente né banda di navigazione (doc 07 §7.1/§7.2).
//   AdminTools.css — pulsanti (§5.1, §5.3), campi (§8) e spunte, condivisi con
//                    tutta l'app: qui non se ne ridichiara nemmeno uno.
//   Enrolment.css  — il poco che è proprio di queste cinque pagine.
import "../components/HomeShell.css";
import "../components/AdminTools.css";
import "./Enrolment.css";
// LE LINGUE D'AIUTO (cantiere «enrollment» 2). Le parole della pagina vengono
// ora dal dizionario unico — sono le stesse, alla lettera — e sotto ciascuna
// può comparire una riga d'aiuto in arabo, cinese o hindi. Chi non tocca
// l'interruttore vede la pagina di prima, e non scarica un byte in più.
import { testo as t } from "./enrolLingue";
import { Aiuto, BarraLingue, ProvvistaLingua, RigaAvviso } from "./enrolAiuto";

// I SOLI campi di D5 per l'adulto. Questo oggetto è anche l'elenco di ciò che
// parte verso il server: quello che non c'è qui non si raccoglie e non si manda.
const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  dob: "",
  gender: "",
  role: "",
  acceptTerms: false,
};

// Gli unici due ruoli che `POST /enrol` accetta: chi ha figli passa dal
// percorso famiglia, e PARENT non è assegnabile da qui.
const RUOLI = ["Player", "Coach"];

// LA CHIAVE DEL MINORENNE FERMATO — l'identità di quel messaggio.
//
// ⚠️ QUI SI CHIUDE UN DIFETTO DI FONDO (cantiere 2, punto 6). RL-4 teneva la
// frase come stringa dentro `primoErroreDelModulo`; RL-5b le affiancò un VARCO
// cliccabile verso il ramo minori, e per sapere QUANDO mostrarlo confrontò
// l'errore in corso con la frase — `error === ERRORE_MINORENNE`. La costante in
// un esemplare solo evitava le due copie, ma non il vizio: l'IDENTITÀ di quel
// messaggio era il suo TESTO. Bastava correggere una virgola in due punti
// diversi, o mostrare la frase in un'altra lingua, e il confronto smetteva di
// riuscire — il varco spariva SENZA UN ERRORE, e nessuna prova lo avrebbe
// detto.
//
// Ora ogni errore del modulo viaggia come AVVISO — `{ chiave, ... }` — e la
// chiave è la sua identità. Il confronto è `errore.chiave === CHIAVE_MINORENNE`
// e non guarda più nemmeno una parola: le parole possono cambiare, essere
// tradotte, essere riscritte, e il varco resta appeso allo stesso caso.
// Le parole di questa chiave stanno nel dizionario (enrolLingue.js), invariate.
const CHIAVE_MINORENNE = "enrol.self.err.minor";

// Il varco verso il ramo minori: stesso trattamento di `TornaAllaScelta`, cioè
// il pulsante FANTASMA del doc 07 §5.3 (trasparente, bordo e testo oro). Prima
// era un testo cliccabile con `#fbc02d` scritto a mano nello stile in linea, un
// oro che nella palette non esiste (§1.4). Non porta a un altro indirizzo e non
// passa nulla: fa cambiare ramo alla pagina, e il modulo famiglia parte dal suo
// stato vuoto.
function VarcoAiMinori({ onVaiAiMinori }) {
  return (
    <>
      <button
        type="button"
        className="qpsc-ghost-btn qpsc-en-ghost"
        onClick={onVaiAiMinori}
      >
        {t("btn.gotoFamily")} &rarr;
      </button>
      <Aiuto k="btn.gotoFamily" colore="oro" />
    </>
  );
}

// Il primo errore del modulo, o `null` se si può inviare.
//
// ⚠️ NON TORNA PIÙ UNA FRASE, TORNA UN AVVISO: `{ chiave }`, dove la chiave
// dice QUALE errore è e le parole stanno nel dizionario. I casi, il loro
// ordine e le condizioni che li fanno scattare sono esattamente quelli di
// prima — questa funzione non ha cambiato una sola regola di validazione.
function primoErroreDelModulo(form) {
  if (
    !form.firstName.trim() ||
    !form.lastName.trim() ||
    !form.email.trim() ||
    !form.dob ||
    !form.gender ||
    !form.role
  ) {
    return { chiave: "enrol.self.err.required" };
  }

  if (!emailBenFormata(form.email)) {
    return { chiave: "enrol.self.err.email" };
  }

  if (!GENERI.some((g) => g.valore === form.gender)) {
    return { chiave: "enrol.self.err.gender" };
  }

  if (!RUOLI.includes(form.role)) {
    return { chiave: "enrol.self.err.role" };
  }

  const eta = etaCompiuta(form.dob, oggiAPerth());

  if (eta === null || eta > 120) {
    return { chiave: "enrol.self.err.dob" };
  }

  if (eta < 0) {
    return { chiave: "enrol.self.err.dobFuture" };
  }

  // Stessa legge del server, detta prima: un minore non si iscrive da sé.
  if (eta < 18) {
    return { chiave: CHIAVE_MINORENNE };
  }

  if (!form.acceptTerms) {
    return { chiave: "err.terms" };
  }

  return null;
}

// IL RAMO ADULTO — il percorso A di RL-4, invariato nel comportamento.
//
// Cambia solo dove vive lo stato del modulo (`form`/`setForm` arrivano dalla
// pagina, per non svuotarlo a ogni ritorno alla scelta) e si aggiunge il
// «torna alla scelta». Campi, validazioni, chiamata a `POST /enrol`, messaggi
// e pannello di conferma sono quelli di RL-4, riga per riga.
function RamoAdulto({ form, setForm, onIndietro, onVaiAiMinori }) {
  const navigate = useNavigate();

  const [touched, setTouched] = useState({});
  // ⚠️ Cantiere 2: `error` non tiene più la frase, tiene l'AVVISO — vedi
  // `CHIAVE_MINORENNE` più sopra. Vuoto = `null`.
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  // La risposta 201 di /enrol: { id, role, status, enrolled }. Se c'è, il
  // modulo lascia il posto al pannello di conferma.
  const [esito, setEsito] = useState(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const handleBlur = (e) =>
    setTouched((prev) => ({ ...prev, [e.target.name]: true }));

  const getFieldClass = (name) => {
    if (!touched[name]) return "";
    return form[name] ? "input-filled" : "input-error";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const problema = primoErroreDelModulo(form);
    if (problema) {
      setError(problema);
      return;
    }

    setSaving(true);

    try {
      // ⚠️ ELENCO CHIUSO, come sul server: partono i soli campi di D5.
      // Nessun `id`, nessun `firebaseUid`, nessun `status`: identità, status e
      // minore età li decide il server.
      const risposta = await api.post("/enrol", {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        dob: form.dob,
        gender: form.gender,
        role: form.role,
        acceptTerms: form.acceptTerms,
      });

      setEsito(risposta || {});
      setForm(initialForm);
      setTouched({});
    } catch (err) {
      setError(messaggioDiErrore(err));
    } finally {
      setSaving(false);
    }
  };

  // ATTERRAGGIO DOPO IL 201.
  //
  // Non c'è accesso automatico: l'iscrizione non crea nessuna utenza Firebase.
  // Il pannello prende il posto del modulo e dice l'unica cosa vera —
  // l'iscrizione è arrivata ed è in attesa, e l'accesso si prende da sé dalla
  // pagina di accesso, col link via email (RL-8b, «strada A»): non serve
  // aspettare che il club approvi, perché fino all'approvazione si atterra
  // sulla vista d'attesa /home/pending.
  //
  // ⚠️ RL-8c: qui si prometteva «an email with the instructions to access the
  // app», e quell'email NON PARTE — il backend, a iscrizione riuscita, manda
  // la sola conferma con il riepilogo dei termini (server.js, POST /enrol) e
  // nient'altro, né allora né all'approvazione. Chi leggeva restava ad
  // aspettare una posta che non sarebbe arrivata mai. Non si promettano email
  // che nessuno spedisce.
  if (esito) {
    return (
      <div className="qpsc-page">
        <main className="qpsc-column">
          {/* Nessun avviso: qui non c'è più niente da compilare. */}
          <BarraLingue />

          <header className="qpsc-en-head">
            <img
              src="/images/logo.png"
              alt="QPSC Logo"
              className="qpsc-en-logo"
            />
            <h2 className="qpsc-page-title">
              {t("done.title")}
              <Aiuto k="done.title" colore="titolo" />
            </h2>
          </header>

          <p className="qpsc-en-msg qpsc-en-msg--ok">
            {t("enrol.self.done.thanks")}
            <Aiuto k="enrol.self.done.thanks" colore="ok" />
          </p>

          {/* ⚠️ IL <strong> RESTA SULL'INGLESE, e con lui l'inglese di questa
              frase: il dizionario porta la sola riga d'aiuto (en: null). Una
              seconda copia dell'inglese qui e nel dizionario divergerebbe
              alla prima correzione. */}
          <p className="qpsc-body-text">
            Your enrolment is now <strong>PENDING</strong>: a club officer will
            review it. You can sign in now with the email address you entered —
            until your enrolment is approved you will see a waiting screen.
            <Aiuto k="enrol.self.done.pending" colore="corpo" />
          </p>

          <p className="qpsc-body-text">
            {t("enrol.self.done.mail")}
            <Aiuto k="enrol.self.done.mail" colore="corpo" />
          </p>

          {esito.id && (
            <p className="qpsc-note">
              {t("done.ref", { id: esito.id })}
              <Aiuto k="done.ref" valori={{ id: esito.id }} />
              <br />
              {t("done.refQuote")}
              <Aiuto k="done.refQuote" />
            </p>
          )}

          <button
            className="qpsc-tool-btn qpsc-tool-btn--wide"
            type="button"
            onClick={() => navigate("/login")}
          >
            {t("btn.backToSignInPage")}
          </button>
          <Aiuto k="btn.backToSignInPage" colore="corpo" centrato />
        </main>
      </div>
    );
  }

  return (
    <div className="qpsc-page">
      <main className="qpsc-column">
        {/* L'USCITA. Era una ✕ che, non avendo CSS proprio, ricadeva sul tag
            nudo `button` di pages/UsersList.css e si mostrava come un quadrato
            rosso in alto a sinistra. Ora è l'azione di servizio del doc 07
            §5.3, e dice a parole dove porta.
            ⚠️ LA NAVIGAZIONE NON CAMBIA: porta al login e non indietro nella
            cronologia, perché qui si arriva spesso da un collegamento esterno
            e «indietro» uscirebbe dall'app. */}
        {/* L'INTERRUTTORE DI LINGUA, in testa al guscio (mockup approvato).
            SENZA AVVISO: nel mockup la riga d'avviso sta sulle sole due
            schermate d'ingresso — l'accesso e la scelta del percorso. Chi è
            arrivato fin qui l'ha già letta, e il modulo non la ripete. */}
        <BarraLingue />

        <button
          className="qpsc-ghost-btn qpsc-en-ghost"
          title="Cancel and go back"
          type="button"
          onClick={() => navigate("/login")}
        >
          &larr; {t("btn.backToSignIn")}
        </button>
        <Aiuto k="btn.backToSignIn" colore="oro" />

        <header className="qpsc-en-head">
          <img
            src="/images/logo.png"
            alt="QPSC Logo"
            className="qpsc-en-logo"
          />
          <h2 className="qpsc-page-title">
            {t("enrol.title")}
            <Aiuto k="enrol.title" colore="titolo" />
          </h2>
        </header>

        {/* RL-6, micro-ritocco b: allineamento al ramo famiglia, dove il
            `disabled` era già entrato in RL-5b. A invio in volo il ritorno
            alla scelta era ancora premibile, e chi lo premeva si vedeva
            sparire il modulo con la richiesta già partita. Durante `saving`
            il varco è chiuso, come il pulsante d'invio. */}
        <TornaAllaScelta onIndietro={onIndietro} disabled={saving} />

        {/* RL-5b: la seconda frase non è più un vicolo cieco. Diceva al
            minorenne che cosa avrebbe dovuto fare senza dargli il modo di
            farlo; ora la via è a un clic, e resta nella stessa pagina.
            Il varco è USCITO dal paragrafo (prima stava dopo un <br/>): è un
            pulsante, e come tale è un blocco proprio della colonna. Il testo
            non è cambiato di una parola. */}
        <p className="qpsc-body-text">
          {t("enrol.self.intro")}
          <Aiuto k="enrol.self.intro" colore="corpo" />
        </p>
        <VarcoAiMinori onVaiAiMinori={onVaiAiMinori} />

        {/* Il minorenne fermato dalla validazione trova qui il varco, accanto
            al motivo dello stop: è il punto in cui sta guardando.
            ⚠️ IL CONFRONTO È SULLA CHIAVE, non sul testo: vedi il commento di
            `CHIAVE_MINORENNE`. I casi in cui il varco compare sono gli stessi
            di prima — l'errore del minorenne, e nessun altro. */}
        {error && (
          <>
            <RigaAvviso
              avviso={error}
              className="qpsc-en-msg qpsc-en-msg--error"
              colore="errore"
            />
            {error.chiave === CHIAVE_MINORENNE && (
              <VarcoAiMinori onVaiAiMinori={onVaiAiMinori} />
            )}
          </>
        )}

        <form className="qpsc-en-form" onSubmit={handleSubmit} autoComplete="off">
          {/* §8: etichetta SOPRA il campo, e la coppia etichetta+campo dentro
              una sola <label> — così toccare l'etichetta porta al campo, che
              con l'etichetta staccata di prima non accadeva. */}
          <label className="qpsc-field">
            <span className="qpsc-field__label">{t("field.firstName")}</span>
            <Aiuto k="field.firstName" />
            <input
              name="firstName"
              value={form.firstName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`qpsc-input ${getFieldClass("firstName")}`}
              required
            />
          </label>

          <label className="qpsc-field">
            <span className="qpsc-field__label">{t("field.lastName")}</span>
            <Aiuto k="field.lastName" />
            <input
              name="lastName"
              value={form.lastName}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`qpsc-input ${getFieldClass("lastName")}`}
              required
            />
          </label>

          <label className="qpsc-field">
            <span className="qpsc-field__label">{t("field.email")}</span>
            <Aiuto k="field.email" />
            <input
              type="email"
              name="email"
              value={form.email}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`qpsc-input ${getFieldClass("email")}`}
              required
            />
          </label>

          <label className="qpsc-field">
            <span className="qpsc-field__label">{t("field.dob")}</span>
            <Aiuto k="field.dob" />
            <input
              type="date"
              name="dob"
              value={form.dob}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`qpsc-input ${getFieldClass("dob")}`}
              required
            />
          </label>

          <label className="qpsc-field">
            <span className="qpsc-field__label">{t("field.gender")}</span>
            {/* ⚠️ LE VOCI DELLA TENDINA RESTANO IN INGLESE. Un <option> non
                può portare una seconda riga, e il mockup approvato le mostra
                infatti non tradotte: chi legge capisce di che campo si tratta
                dall'etichetta qui sopra, che l'aiuto traduce. */}
            <Aiuto k="field.gender" />
            <select
              name="gender"
              value={form.gender}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`qpsc-select ${getFieldClass("gender")}`}
              required
            >
              <option value="">Select</option>
              {GENERI.map((g) => (
                <option key={g.valore} value={g.valore}>
                  {g.etichetta}
                </option>
              ))}
            </select>
          </label>

          <label className="qpsc-field">
            <span className="qpsc-field__label">{t("field.role")}</span>
            <Aiuto k="field.role" />
            <select
              name="role"
              value={form.role}
              onChange={handleChange}
              onBlur={handleBlur}
              className={`qpsc-select ${getFieldClass("role")}`}
              required
            >
              <option value="">Select</option>
              {RUOLI.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>

          {/* Stessi documenti e stessa formula del modulo in esercizio: il
              momento in cui i termini si accettano cambia sede, non testo.
              La spunta è quella condivisa del §8 (AdminTools.css): il quadrato
              lo disegna uno <span>, e l'<input> resta il comando vero, solo
              trasparente — è la difesa dai tag nudi dei fogli d'eredità, che
              su una checkbox facevano un rettangolo da 80px. */}
          <label className="qpsc-check">
            <input
              className="qpsc-check__input"
              type="checkbox"
              name="acceptTerms"
              checked={form.acceptTerms}
              onChange={handleChange}
            />
            <span className="qpsc-check__box" aria-hidden="true" />
            <LinkTermini />
          </label>

          {/* Si dice PRIMA dell'invio che cosa succede dopo: il modulo vecchio
              prometteva un accesso immediato che qui non esiste. */}
          <p className="qpsc-note">
            No password is required: your enrolment is reviewed by the club,
            and you will receive a confirmation email from the club.
          </p>

          <button
            className="qpsc-tool-btn qpsc-tool-btn--wide"
            type="submit"
            disabled={saving}
          >
            {saving ? t("btn.sending") : t("btn.send")}
          </button>
          <Aiuto
            k={saving ? "btn.sending" : "btn.send"}
            colore="corpo"
            centrato
          />
        </form>
      </main>
    </div>
  );
}

// IL PRIMO PASSO — LA SCELTA DEL PERCORSO (decisione Q2, strada (b)).
//
// Due voci sole, e dicono MINORI, non «famiglia»: chi arriva qui deve capire
// dalla prima riga quale delle due lo riguarda, e «famiglia» è una parola che
// un adulto senza figli legge come «non mia» e un genitore che si iscrive per
// sé legge come «mia». Le due voci sono oggi le opzioni di un GRUPPO-SCELTA
// (doc 07 §6): pulsanti scuri §5.2 dentro un contenitore bordato d'oro.
//
// ⚠️ Le schede sono cliccabili anche da tastiera (Invio e barra spaziatrice):
// il modulo vecchio le aveva come semplici `div` con `onClick`, e chi naviga
// senza mouse non poteva iscriversi affatto.
function SceltaPercorso({ onScegli }) {
  const navigate = useNavigate();

  const daTastiera = (percorso) => (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onScegli(percorso);
    }
  };

  return (
    <div className="qpsc-page">
      <main className="qpsc-column">
        {/* L'INTERRUTTORE DI LINGUA, in testa al guscio (mockup approvato). */}
        <BarraLingue avviso />

        {/* Stessa uscita del ramo adulto, stessa destinazione: /login. */}
        <button
          className="qpsc-ghost-btn qpsc-en-ghost"
          title="Cancel and go back"
          type="button"
          onClick={() => navigate("/login")}
        >
          &larr; {t("btn.backToSignIn")}
        </button>
        <Aiuto k="btn.backToSignIn" colore="oro" />

        <header className="qpsc-en-head">
          <img
            src="/images/logo.png"
            alt="QPSC Logo"
            className="qpsc-en-logo"
          />
          <h2 className="qpsc-page-title">
            {t("enrol.title")}
            <Aiuto k="enrol.title" colore="titolo" />
          </h2>
        </header>

        {/* IL GRUPPO-SCELTA del doc 07 §6: le due vie sono i rami di UN bivio,
            non due voci indipendenti, quindi stanno dentro un contenitore
            bordato d'oro con l'etichetta del gruppo in alto.
            ⚠️ L'etichetta è l'unica stringa NUOVA di questo cantiere: il §6 la
            pretende («il gruppo è concettualmente UN pulsante») e prima non
            c'era. Tutto il resto del testo è quello di sempre.
            Le due pastiglie «For myself» / «For children» sono invece sparite:
            il titolo dell'opzione dice già la stessa cosa per esteso, e il §2
            riserva la pastiglia alle cinque categorie anagrafiche. */}
        <div className="qpsc-choice-group">
          <span className="qpsc-choice-group__label">
            {t("enrol.choice.group")}
            <Aiuto k="enrol.choice.group" colore="oro" />
          </span>

          {/* Restano `<div role="button">` con `tabIndex`, com'erano: la
              pressione da tastiera (Invio e barra spaziatrice) è funzione, e
              questo cantiere non tocca le funzioni. La veste è quella del
              pulsante SCURO §5.2, la stessa delle opzioni di ogni altro
              gruppo-scelta dell'app. */}
          <div
            className="qpsc-btn-dark qpsc-en-option"
            role="button"
            tabIndex={0}
            onClick={() => onScegli("adulto")}
            onKeyDown={daTastiera("adulto")}
          >
            <h3 className="qpsc-en-option__title">
              {t("enrol.choice.self.title")}
              <Aiuto k="enrol.choice.self.title" colore="oro-chiaro" />
            </h3>

            <p className="qpsc-en-option__help">
              {t("enrol.choice.self.help")}
              <Aiuto k="enrol.choice.self.help" />
            </p>
          </div>

          <div
            className="qpsc-btn-dark qpsc-en-option"
            role="button"
            tabIndex={0}
            onClick={() => onScegli("minori")}
            onKeyDown={daTastiera("minori")}
          >
            <h3 className="qpsc-en-option__title">
              {t("enrol.choice.children.title")}
              <Aiuto k="enrol.choice.children.title" colore="oro-chiaro" />
            </h3>

            <p className="qpsc-en-option__help">
              {t("enrol.choice.children.help")}
              <Aiuto k="enrol.choice.children.help" />
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}

// LA PAGINA. Tiene la scelta e — di proposito — lo stato dei due moduli: chi
// sbaglia percorso e torna indietro ritrova quello che aveva scritto, invece
// di ricominciare da capo. Quello che NON sopravvive al cambio di ramo è solo
// l'effimero (campi toccati, errore mostrato, invio in corso), che ricompare
// da sé al primo tocco.
// Il contenitore di stato della lingua d'aiuto avvolge la PAGINA, e non i
// singoli rami: chi sceglie l'arabo sulla schermata di scelta se lo ritrova
// dentro il modulo, e nel ramo famiglia, senza ripremere niente. Non aggiunge
// markup e non tocca nessuna funzione.
export default function Enrol() {
  return (
    <ProvvistaLingua>
      <PaginaEnrol />
    </ProvvistaLingua>
  );
}

function PaginaEnrol() {
  const [percorso, setPercorso] = useState(null);
  const [formAdulto, setFormAdulto] = useState(initialForm);
  const [formFamiglia, setFormFamiglia] = useState(famigliaVuota);

  const allaScelta = () => setPercorso(null);

  if (percorso === "adulto") {
    return (
      <RamoAdulto
        form={formAdulto}
        setForm={setFormAdulto}
        onIndietro={allaScelta}
        // Il varco del minorenne (RL-5b): cambia SOLO il ramo mostrato.
        // `formFamiglia` non viene toccato, quindi il modulo minori parte
        // vuoto — nessun dato del ramo adulto (né la data di nascita né
        // altro) viene trascinato dentro l'iscrizione di un minore.
        onVaiAiMinori={() => setPercorso("minori")}
      />
    );
  }

  if (percorso === "minori") {
    return (
      <EnrolFamily
        form={formFamiglia}
        setForm={setFormFamiglia}
        onIndietro={allaScelta}
      />
    );
  }

  return <SceltaPercorso onScegli={setPercorso} />;
}
